import {createTheme, experimental_sx as sx} from '@mui/material';

export const palette = {
    white: '#fff',
    gray: {
        100: '#F4F5F7',
        400: '#8e8e8e',
        500: '#5d5d5d',
    },
    sky: {
        50: '#f0f9ff',
        100: '#e0f2fe',
        200: '#bae6fd',
        300: '#7dd3fc',
        400: '#38bdf8',
        500: '#0ea5e9',
        600: '#0284c7',
        700: '#0369a1',
        800: '#075985',
        900: '#0c4a6e',
        950: '#082f49',
    },
    yellow: {
        50: '#fefce8',
        100: '#fef9c3',
        200: '#fef08a',
        300: '#fde047',
        400: '#facc15',
        500: '#eab308',
        600: '#ca8a04',
        700: '#a16207',
        800: '#854d0e',
        900: '#713f12',
        950: '#422006',
    },
    green: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
        950: '#052e16',
    },
    primary: {
        300: '#edfcf2',
        400: '#9cedb7',
        500: '#5ae288',
        600: '#4fc878',
        700: '#48b56d',
    },
    secondary: {
        100: '#e8edec',
        200: '#bac9c7',
        300: '#3a645f',
        400: '#5D847F',
        500: '#174943',
    },
    tertiary: {
        400: '#fcfbf3',
        500: '#f4f0d7',
        600: '#272b4d',
        800: '#745E05',
    },
};

export const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1440,
        },
    },
    typography: {
        h1: {
            fontSize: '72px',
            fontFamily: 'Lora',
            fontWeight: 400,
            color: palette.secondary[500],
        },
        h2: {
            fontSize: '54px',
            fontFamily: 'Lora',
            fontWeight: 400,
            color: palette.secondary[500],
        },
        h3: {
            fontSize: '48px',
            fontFamily: 'Lora',
            fontWeight: 400,
            color: palette.secondary[500],
            '@media (max-width: 600px)': {
                fontSize: '28px',
            },
        },
        h4: {
            fontSize: '24px',
            fontFamily: 'Lora',
            fontWeight: 400,
            color: palette.secondary[500],
        },
        button: {
            fontSize: '28px',
            fontFamily: 'Lora',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '120%',
            cursor: 'pointer',
            textTransform: 'none',
            color: palette.tertiary[600],
            '@media (max-width: 900px)': {
                fontSize: '24px',
            },
        },
        subtitle1: {
            fontFamily: 'Fredoka',
            fontWeight: 300,
            fontSize: '26px',
            lineHeight: '150%',
            color: palette.secondary[500],
            '@media (max-width: 600px)': {
                fontSize: '18px',
            },
        },
        subtitle2: {
            fontFamily: 'Fredoka',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '150%',
        },
        body1: {
            fontFamily: 'Fredoka',
            fontSize: '1rem',
            color: '#5C5C5C',
        },
        body2: {
            fontFamily: 'Fredoka',
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: '160%',
            color: palette.secondary[500],
        },
    },
    palette: {
        mode: 'light',
        primary: {
            main: palette.primary[500],
            light: palette.primary[400],
            dark: palette.primary[600],
            contrastText: palette.secondary[500],
        },
        secondary: {
            main: palette.secondary[500],
            light: palette.secondary[300],
            dark: palette.secondary[500],
            contrastText: palette.white,
        },
        warning: {
            main: palette.tertiary[400],
            light: palette.tertiary[400],
            dark: palette.tertiary[800],
            contrastText: palette.tertiary[800],
        },
        info: {
            main: palette.gray[400],
            light: palette.gray[400],
            dark: palette.gray[500],
            contrastText: palette.gray[500],
        },
        sky: {
            main: palette.sky[400],
            light: palette.sky[400],
            dark: palette.sky[500],
            contrastText: palette.sky[800],
        },
        yellow: {
            main: palette.yellow[400],
            light: palette.yellow[400],
            dark: palette.yellow[500],
            contrastText: palette.yellow[800],
        },
        green: {
            main: palette.green[400],
            light: palette.green[400],
            dark: palette.green[500],
            contrastText: palette.green[800],
        },
    },
    /**
     * Care must be taken when using style overrides in order to fully retain
     * the usage of color and size props. When overriding styles, prefer to
     * override sizing information within a size variant. For instance, a button
     * component has a root which is applied to all button types. In order to
     * adjust sizing, override styles in one of the `size` prefixed namespaces.
     * To fine tune those sizes for variants, modify a `{variant}Size{size}`
     * namespace. In order to adjust color, modify styles in one of
     * `{variant}{color}` namespaces.
     *
     * Example: Apply a padding change to all large buttons
     *
     * {
     *   components: {
     *     MuiButton: {
     *       styleOverrides: {
     *         sizeLarge: {
     *           padding: '30px'
     *         }
     *       }
     *     }
     *   }
     * }
     *
     * Example: Apply a padding change to all small contained buttons
     *
     * {
     *   components: {
     *     MuiButton: {
     *       styleOverrides: {
     *         containedSizeSmall: {
     *           padding: '30px'
     *         }
     *       }
     *     }
     *   }
     * }
     */
    components: {
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    borderColor: palette.secondary[500],
                    '&:hover span': {
                        borderColor: palette.secondary[500],
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    color: palette.secondary[500],
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    minHeight: '64px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 0,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 0,
                        borderColor: palette.secondary[500],
                        borderWidth: 1,
                    },
                    borderRadius: 0,
                    fieldset: {
                        borderColor: palette.secondary[500],
                        legend: {
                            fontSize: '14px',
                            backgroundColor: palette.white,
                            visibility: 'visible',
                            '& > span': {
                                visibility: 'hidden',
                            },
                        },
                    },
                },
                input: {
                    fontFamily: 'Fredoka',
                    fontSize: '18px',

                    '&:disabled': {
                        opacity: 0.3,
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.MuiInputLabel-root': {
                        color: palette.secondary[500],
                        fontFamily: 'Fredoka',
                        fontWeight: 400,
                        fontSize: '18px',

                        '&.Mui-disabled': {
                            opacity: 0.3,
                        },
                    },
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                MenuProps: {},
            },
            styleOverrides: {},
        },
        MuiAutocomplete: {
            styleOverrides: {
                option: {
                    '&[aria-selected="true"]': {
                        backgroundColor: `${palette.primary[400]} !important`,
                        '&.Mui-focused': {
                            backgroundColor: `${palette.primary[400]} !important`,
                        },
                    },
                    '&.Mui-focused': {
                        backgroundColor: `${palette.primary[300]} !important`,
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: sx({
                    '&:hover': {
                        backgroundColor: palette.primary[300],
                    },
                    '&.Mui-selected': {
                        backgroundColor: palette.primary[400],
                    },
                }),
                selected: {},
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    '&:focus': {
                        outline: 'none',
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: '10em',
                    fontFamily: 'Fredoka',
                },
                outlined: sx({
                    borderWidth: '2px',
                    '&:hover': {
                        borderWidth: '2px',
                    },
                }),
                outlinedSecondary: sx({
                    color: palette.secondary[500],
                    borderColor: palette.secondary[500],
                    '&:hover': {
                        color: palette.white,
                        backgroundColor: palette.secondary[500],
                    },
                }),
                containedPrimary: sx({
                    color: palette.secondary[500],
                    '&:hover': {
                        color: palette.secondary[500],
                        backgroundColor: palette.primary[400],
                    },
                }),
                containedSecondary: sx({
                    color: palette.white,
                    '&:hover': {
                        color: palette.white,
                        backgroundColor: palette.secondary[300],
                    },
                }),
                sizeLarge: sx({
                    fontSize: '20px',
                    lineHeight: '150%',
                    px: '56px',
                    height: '72px',
                }),
                sizeMedium: sx({
                    fontSize: '20px',
                    lineHeight: '150%',
                    px: '56px',
                    height: '60px',
                }),
                sizeSmall: sx({
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '150%',
                    px: '26px',
                    height: '41px',
                }),
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: sx({
                    fontFamily: 'Fredoka',
                    borderRadius: '8px',
                    padding: '11px 24px',
                    fontSize: '16px',
                    lineHeight: '22.4px',
                }),
                filledWarning: {
                    color: palette.tertiary[800],
                },
                filledSuccess: {
                    color: palette.secondary[500],
                    backgroundColor: palette.primary[300],
                },
            },
        },
    },
});
